import React from 'react';

import {segmentStyle, segmentedGroupStyle} from '../lib/formStyles.js';

// ============================================================================
// Segmented — one choice from a few (Design Constitution I05-B, Q01, Q41)
// ----------------------------------------------------------------------------
// The selected segment is solid #187A54 with white text; segmented controls and
// Yes/No are the only solid selected states (Q01). Tapping the selected segment
// again keeps it selected: there is no tap-again clearing (Q41). When the
// field may be left empty, noneLabel adds a 'None' segment that sets ''.
// Segments pop out like any clickable (§2). The group takes the id / aria
// wiring a <Field> hands its child, including aria-labelledby for its name.
// ============================================================================

export type SegmentedOption = {value: string; label: React.ReactNode};

type SegmentedProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> & {
  options: Array<string | SegmentedOption>;
  value: string;
  onChange: (next: string) => void;
  noneLabel?: string;
  required?: boolean;
};

export default function Segmented({options, value, onChange, noneLabel, required, style, ...rest}: SegmentedProps) {
  const list: SegmentedOption[] = options.map((o) => (typeof o === 'string' ? {value: o, label: o} : o));
  if (noneLabel) list.push({value: '', label: noneLabel});
  return (
    <div
      {...rest}
      role="group"
      aria-required={required || undefined}
      data-segmented="1"
      style={{...segmentedGroupStyle, ...style}}
    >
      {list.map((o, i) => {
        const selected = value === o.value;
        return (
          <button
            key={o.value === '' ? '__none' : o.value}
            type="button"
            className="wcf-clickable"
            aria-pressed={selected}
            data-segment={o.value === '' ? 'none' : o.value}
            onClick={() => {
              if (!selected) onChange(o.value);
            }}
            style={segmentStyle(selected, i === 0, i === list.length - 1)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

type YesNoProps = Omit<React.HTMLAttributes<HTMLDivElement>, 'onChange'> & {
  value: boolean | null | undefined;
  onChange: (next: boolean) => void;
  required?: boolean;
};

// Yes / No as a two-segment control (I05-B). Either answer is solid green when
// chosen; an unanswered question shows neither.
export function YesNo({value, onChange, ...rest}: YesNoProps) {
  return (
    <Segmented
      {...rest}
      data-yes-no="1"
      options={[
        {value: 'yes', label: 'Yes'},
        {value: 'no', label: 'No'},
      ]}
      value={value === true ? 'yes' : value === false ? 'no' : ''}
      onChange={(v) => onChange(v === 'yes')}
    />
  );
}
